import { ThemeProvider } from '@react-navigation/native';
import { StatusBar } from 'expo-status-bar';
import { useFonts } from 'expo-font';
import * as Linking from 'expo-linking';
import { Stack } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { useEffect } from 'react';
import { AppState, Platform } from 'react-native';
import 'react-native-reanimated';
import { PaperProvider } from 'react-native-paper';

import Onboarding from '@/components/Onboarding';
import {
  navigationDarkTheme,
  navigationLightTheme,
  paperDarkTheme,
  paperLightTheme,
  useResolvedScheme,
} from '@/constants/theme';
import { handleSupabaseAuthRedirect } from '@/lib/authRedirect';
import { isSupabaseConfigured, supabase } from '@/lib/supabase';
import { useAppStore } from '@/stores/useStore';

export {
  ErrorBoundary,
} from 'expo-router';

export const unstable_settings = {
  initialRouteName: '(tabs)',
};

SplashScreen.preventAutoHideAsync();

export default function RootLayout() {
  const [loaded, error] = useFonts({
    SpaceMono: require('../assets/fonts/SpaceMono-Regular.ttf'),
  });

  useEffect(() => {
    if (error) throw error;
  }, [error]);

  useEffect(() => {
    if (loaded) {
      SplashScreen.hideAsync();
    }
  }, [loaded]);

  if (!loaded) {
    return null;
  }

  return <RootLayoutNav />;
}

function RootLayoutNav() {
  const loadCurrentUser = useAppStore((state) => state.loadCurrentUser);
  const hasHydrated = useAppStore((state) => state.hasHydrated);
  const hasSeenOnboarding = useAppStore((state) => state.hasSeenOnboarding);
  const completeOnboarding = useAppStore((state) => state.completeOnboarding);
  const scheme = useResolvedScheme();
  const isDark = scheme === "dark";

  useEffect(() => {
    const handleAuthUrl = async (url: string | null) => {
      await handleSupabaseAuthRedirect(url, loadCurrentUser);
    };

    Linking.getInitialURL()
      .then(handleAuthUrl)
      .catch((error) => {
        console.warn("Failed to read initial auth URL", error);
      });

    const subscription = Linking.addEventListener("url", ({ url }) => {
      handleAuthUrl(url).catch((error) => {
        console.warn("Failed to handle auth URL", error);
      });
    });

    return () => {
      subscription.remove();
    };
  }, [loadCurrentUser]);

  useEffect(() => {
    loadCurrentUser().catch((error) => {
      console.warn("Failed to load current user", error);
    });
  }, [loadCurrentUser]);

  useEffect(() => {
    if (!isSupabaseConfigured || Platform.OS === "web") return;

    const subscription = AppState.addEventListener("change", (state) => {
      if (state === "active") {
        supabase.auth.startAutoRefresh();
        return;
      }

      supabase.auth.stopAutoRefresh();
    });

    supabase.auth.startAutoRefresh();

    return () => {
      subscription.remove();
      supabase.auth.stopAutoRefresh();
    };
  }, []);

  return (
    <PaperProvider theme={isDark ? paperDarkTheme : paperLightTheme}>
      <ThemeProvider value={isDark ? navigationDarkTheme : navigationLightTheme}>
        <StatusBar style={isDark ? "light" : "dark"} />
        <Stack>
          <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
        </Stack>
        {hasHydrated && !hasSeenOnboarding && (
          <Onboarding onDone={completeOnboarding} />
        )}
      </ThemeProvider>
    </PaperProvider>
  );
}
